import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function AdminStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [filterCategory]);

  const fetchStudents = async () => {
    try {
      let url = '/admin/students?';
      if (filterCategory) url += `category=${filterCategory}&`;
      if (searchTerm) url += `search=${searchTerm}`;

      const res = await api.get(url);
      setStudents(res.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (studentId) => {
    try {
      const res = await api.get(`/admin/students/${studentId}`);
      setSelectedStudent(res.data);
      setShowDetailModal(true);
    } catch (error) {
      alert('Error loading student details');
    }
  };

  const handleSearch = () => {
    fetchStudents();
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-3">
        <div className="col">
          <h2>Student Management</h2>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-2">
          <select 
            className="form-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            <option value="A">Category A</option>
            <option value="B">Category B</option>
            <option value="C">Category C</option>
            <option value="Unassigned">Unassigned</option>
          </select>
        </div>
        <div className="col-md-6">
          <div className="input-group">
            <input 
              type="text" 
              className="form-control"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-primary" onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>
        <div className="col-md-4">
          <button className="btn btn-secondary" onClick={() => {
            setFilterCategory('');
            setSearchTerm('');
            fetchStudents();
          }}>
            Clear Filters
          </button>
          <span className="ms-3 text-muted">
            Total Students: {students.length}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border"></div></div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Category</th>
                <th>Skill Tier</th>
                <th>Screening Score</th>
                <th>Enrollments</th>
                <th>Completed</th>
                <th>Badges</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <tr key={student._id}>
                  <td><strong>{student.name}</strong></td>
                  <td>{student.email}</td>
                  <td>
                    <span className={`badge bg-${
                      student.category === 'A' ? 'success' :
                      student.category === 'B' ? 'info' :
                      student.category === 'C' ? 'warning' :
                      'secondary'
                    }`}>
                      {student.category}
                    </span>
                  </td>
                  <td>{student.skillTier}</td>
                  <td>{student.screeningScore || 0}%</td>
                  <td>{student.stats?.enrollmentCount || 0}</td>
                  <td>{student.stats?.completedCount || 0}</td>
                  <td>{student.badges?.length || 0}</td>
                  <td>{new Date(student.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button 
                      className="btn btn-sm btn-info"
                      onClick={() => viewDetails(student._id)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedStudent && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Student Details</h5>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <h6>Personal Information</h6>
                    <p><strong>Name:</strong> {selectedStudent.user?.name}</p>
                    <p><strong>Email:</strong> {selectedStudent.user?.email}</p>
                    <p><strong>Phone:</strong> {selectedStudent.user?.phone || 'N/A'}</p>
                    <p><strong>Email Verified:</strong> {selectedStudent.user?.isEmailVerified ? 'Yes' : 'No'}</p>
                  </div>
                  <div className="col-md-6">
                    <h6>Academic Information</h6>
                    <p><strong>Category:</strong> {selectedStudent.user?.category}</p>
                    <p><strong>Skill Tier:</strong> {selectedStudent.user?.skillTier}</p>
                    <p><strong>Screening Score:</strong> {selectedStudent.user?.screeningScore}%</p>
                    <p><strong>Rank:</strong> {selectedStudent.user?.rank}</p>
                    <p><strong>Contribution Score:</strong> {selectedStudent.user?.contributionScore}</p>
                  </div>
                </div>

                <h6>Profile Links</h6>
                <p>
                  <strong>GitHub:</strong> {selectedStudent.user?.github || 'N/A'}<br/>
                  <strong>LinkedIn:</strong> {selectedStudent.user?.linkedin || 'N/A'}
                </p>

                {selectedStudent.user?.bio && (
                  <>
                    <h6>Bio</h6>
                    <p>{selectedStudent.user?.bio}</p>
                  </>
                )}

                <h6>Badges ({selectedStudent.user?.badges?.length || 0})</h6>
                <div className="mb-3">
                  {selectedStudent.user?.badges?.map((badge, index) => (
                    <span key={index} className="badge bg-primary me-1 mb-1">{badge}</span>
                  ))}
                  {(!selectedStudent.user?.badges || selectedStudent.user.badges.length === 0) && (
                    <p className="text-muted">No badges earned yet</p>
                  )}
                </div>

                <h6>Enrollments ({selectedStudent.enrollments?.length || 0})</h6>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Program</th>
                        <th>Status</th>
                        <th>Progress</th>
                        <th>Enrolled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedStudent.enrollments?.map((enrollment) => (
                        <tr key={enrollment._id}>
                          <td>{enrollment.program?.title}</td>
                          <td>
                            <span className={`badge bg-${
                              enrollment.enrollmentStatus === 'completed' ? 'success' :
                              enrollment.enrollmentStatus === 'active' ? 'primary' :
                              'warning'
                            }`}>
                              {enrollment.enrollmentStatus}
                            </span>
                          </td>
                          <td>{enrollment.overallProgress || 0}%</td>
                          <td>{new Date(enrollment.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="row mt-3">
                  <div className="col-md-12">
                    <div className="alert alert-info">
                      <strong>Stats:</strong>
                      <ul className="mb-0">
                        <li>Total Enrollments: {selectedStudent.stats?.totalEnrollments || 0}</li>
                        <li>Active Enrollments: {selectedStudent.stats?.activeEnrollments || 0}</li>
                        <li>Completed Enrollments: {selectedStudent.stats?.completedEnrollments || 0}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminStudents;
